import React, { useState, useEffect, useContext } from 'react';
import './PaymentInfo.css';
import { useOrderContext } from '../../../Context/OrderContext'; 
import { ShopContext } from '../../../Context/ShopContext'; 
import PayPalHandler from './PayPalHandler'; 
import StripeService from '../../../Services/StripeService';
import InvoiceService from '../../../Services/InvoiceService';
import { useStripe, useElements, CardElement, CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

const PaymentInfo = ({ onContinue, onBack }) => {
    const { formData } = useOrderContext(); 
    const { cartItems, all_product, getTotalCartAmount, clearCart, updateStock } = useContext(ShopContext); 
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(formData.paymentMethod);
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [cardHolderName, setCardHolderName] = useState(formData.name || "");
    const [loading, setLoading] = useState(false);

    const stripe = useStripe();
    const elements = useElements();

    // Load Stripe with the public key
    const stripePromise = loadStripe('pk_live_51QrOUHCxd2uUqZOpSCv6rBfcoAKYUHgDUhmmITZzb0YJCWzRbb7aqx1gmpuDgzc7JreDbfDOMlPlw8VOIhBCo7rO00qJzMsL87');

    const allProductsMap = all_product.reduce((map, product) => {
        map[product._id] = product;
        return map;
    }, {});

    const processedData = Object.entries(cartItems)
    .filter(([key, { quantity }]) => quantity > 0)
    .map(([key, { quantity, size, color }]) => {
        const [itemId] = key.split('-');
        const product = allProductsMap[itemId];
        if (!product) {
            console.log(`Product not found for key: ${key}`);
            return null;
        }
        const totalPrice = (product.new_price || 0) * quantity;

        // Log the product details
        console.log('Processing Product:', {
            productId: product._id, // Add the productId here
            product: product.name, // Include product name
            quantity,
            size,
            color,
            price: product.new_price,
            totalPrice,
            stock: product.quantity || 'Quantity not available',
        });

        return {
            productId: product._id, // Ensure that we are passing the productId
            product: product.name, // Include product name
            quantity,
            size,
            color,
            price: product.new_price || 0,
            totalPrice,
            stock: product.quantity || 0 
        };
    })
    .filter(Boolean);

    console.log("Processed Cart Items: ", processedData);


    // Check if there's enough stock for the cart items
    const checkStockAvailability = () => {
        for (const item of processedData) {
            console.log(`Checking stock for product: ${item.productId}`);
            console.log(`Available stock: ${item.stock}, Quantity to purchase: ${item.quantity}`);
    
            // Check if the quantity to be purchased exceeds available stock
            if (item.quantity > item.stock) {
                alert(`Not enough stock for ${item.productId}. Only ${item.stock} left.`);
                return false; // Stop the process if stock is insufficient
            }
        }
        return true; // If all items pass the stock check
    };
    

    const handlePaymentMethodChange = (event) => {
        setSelectedPaymentMethod(event.target.value);
    };

    const handlePayPalCheckout = async () => {
        try {
            const totalPrice = getTotalCartAmount() + (formData.shippingFee || 0);
            console.log("Total Price including shipping: ", totalPrice);

            const approvalUrl = await PayPalHandler.createOrder(totalPrice);

            if (!approvalUrl) {
                return;
            }

            const invoiceData = {
                formData,
                selectedPaymentMethod,
                totalPrice,
                shippingFee: formData.shippingFee,
                shippingMethod: formData.shippingMethod,
                cartItems: processedData
            };

            // Create the order object to send to the backend
            const orderData = {
                customerName: `${formData.name} ${formData.lastName}`,
                email: formData.email,
                address: formData.address,
                city: formData.city,
                postalCode: formData.postalCode,
                products: processedData, // Add cartItems here
                shippingFee: formData.shippingFee,
                shippingMethod: formData.shippingMethod || 'standard',
                totalAmount: totalPrice, 
                status: 'pending'
            };

            sessionStorage.setItem('invoiceData', JSON.stringify(invoiceData));

            window.onContinue = async () => {
                setPaymentSuccess(true); 
                InvoiceService.generatePDF({ formData, totalPrice, cartItems: processedData });
                console.log("Order data before sending:", orderData);

                // Create the order in the backend
                await InvoiceService.createOrder({ orderData });

                // Step to update stock levels after payment success
                await updateStock(processedData);
                clearCart();
                onContinue();
            };

            const paypalWindow = PayPalHandler.openPayPalWindow(approvalUrl);
            console.log("PayPal window opened:", paypalWindow);

        } catch (error) {
            console.error("Error during PayPal checkout:", error);
            setPaymentSuccess(false);
        }
    };

    const handleStripeCheckout = async () => {
        setLoading(true);

        // Ensure stripe and elements are loaded before proceeding
        if (!stripe || !elements) {
            console.log("Stripe has not loaded.");
            return;
        }

        const stripeService = new StripeService(
            stripe, elements, formData, getTotalCartAmount, clearCart, onContinue, InvoiceService.generatePDF, processedData, setLoading, updateStock
        );

        stripeService.handleStripePayment();
    };

    const handleConfirmAndPay = () => {
        if (!checkStockAvailability()) {
            return; // Exit if stock is insufficient
        }

        if (selectedPaymentMethod === 'paypal') {
            handlePayPalCheckout();
        } else if (selectedPaymentMethod === 'creditCard'){
            handleStripeCheckout();
        } else {
            alert("Please select a payment method");
            return;
        }
    };

    useEffect(() => {
        if (paymentSuccess) {
            console.log("Paid");
            setLoading(false);
            onContinue();
        } else {
            console.log("Did not pay");
            setLoading(false);
        }
    }, [paymentSuccess]);

    return (
        <div className="form-section">
            <h2>Payment Selection</h2>
            <p>Please select your payment method:</p>

            {/* Credit Card Box */}
            <div className={`payment-box ${selectedPaymentMethod === 'creditCard' ? 'selected' : ''}`}>
                <div className="payment-option">
                    <input
                        type="radio"
                        id="creditCard"
                        name="payment"
                        value="creditCard"
                        checked={selectedPaymentMethod === 'creditCard'}
                        onChange={handlePaymentMethodChange}
                    />
                    <label htmlFor="creditCard">Credit Card</label>
                </div>

                <div className="credit-card-info">
                    <label>Card Details</label>
                    {/* Cardholder's Name Input */}
                    <div className="form-field">
                        <label htmlFor="cardNumber">Card Number</label>
                        <CardNumberElement id="cardNumber" options={{ style: { base: { fontSize: '16px' } } }} />
                    </div>

                    <div className="row">
                        <div className="form-field">
                            <label htmlFor="cardHolderName">Name on Card</label>
                            <input
                                className="input-name"
                                type="text"
                                id="cardHolderName"
                                value={formData.name} // Assuming 'formData.name' holds the user's name
                                onChange={(e) => setCardHolderName(e.target.value)}
                                placeholder="Enter cardholder's name" 
                            />
                        </div>
                        <div className="form-field">
                            <label htmlFor="expiry">Expiry Date</label>
                            <CardExpiryElement id="expiry" options={{ style: { base: { fontSize: '16px' } } }} />
                        </div>
                        <div className="form-field">
                            <label htmlFor="cvc">CVC</label>
                            <CardCvcElement id="cvc" options={{ style: { base: { fontSize: '16px' } } }} />
                        </div>
                    </div>
                </div>
            </div>

            {/* PayPal Box */}
            <div className={`payment-box ${selectedPaymentMethod === 'paypal' ? 'selected' : ''}`}>
                <div className="payment-option">
                    <input
                        type="radio"
                        id="paypal"
                        name="payment"
                        value="paypal"
                        checked={selectedPaymentMethod === 'paypal'}
                        onChange={handlePaymentMethodChange}
                    />
                    <label htmlFor="paypal">PayPal</label>
                </div>

                <div className="paypal-info">
                    <p>You will be redirected to PayPal website to complete your purchase securely.</p>
                </div>
            </div>

            <div className="button-container">
                <button type="button" onClick={onBack} className="back-button">Back to Shipping</button>
                <button type="button" onClick={handleConfirmAndPay} className="continue-button">
                    Confirm and Pay
                </button>
            </div>

            {/* Spinner Modal */}
            {loading && (
                <div className="spinner-modal">
                    <div className="spinner-container">
                        <div className="loading-spinner"></div>
                        <div className="spinner-text">Processing your payment...</div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaymentInfo;
